import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, TrendingDown, AlertTriangle, Maximize2, ArrowRight, Save, Edit2, X, Plus, Trash2, MoveHorizontal, Undo } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

interface Department {
  id: string;
  name: string;
  currentCapacity: number;
  maxCapacity: number;
  peakHours: string;
  utilizationRate: number;
  growthProjection: number;
  collaborationScore: number;
  plannedHeadcount?: number;
  plannedGrowthDate?: string;
}

interface Zone {
  id: string;
  name: string;
  type: 'workspace' | 'meeting' | 'collaboration' | 'amenity';
  capacity: number;
  currentOccupancy: number;
  peakUtilization: number;
  recommendedCapacity: number;
  adjacentZones?: string[];
  x?: number;
  y?: number;
}

interface SimulationMetrics {
  totalCapacity: number;
  utilizationRate: number;
  collaborationScore: number;
  congestionRisk: 'low' | 'medium' | 'high';
  costImpact: number;
}

const OccupancyManager: React.FC = () => {
  // Department capacity data
  const [departments, setDepartments] = useState<Department[]>([
    {
      id: 'eng',
      name: 'Engineering',
      currentCapacity: 85,
      maxCapacity: 100,
      peakHours: '10:00 - 15:00',
      utilizationRate: 78,
      growthProjection: 15,
      collaborationScore: 82
    },
    {
      id: 'sales',
      name: 'Sales',
      currentCapacity: 45,
      maxCapacity: 60,
      peakHours: '9:00 - 16:00',
      utilizationRate: 65,
      growthProjection: 25,
      collaborationScore: 75
    },
    {
      id: 'prod',
      name: 'Product',
      currentCapacity: 35,
      maxCapacity: 40,
      peakHours: '11:00 - 17:00',
      utilizationRate: 85,
      growthProjection: 10,
      collaborationScore: 88
    }
  ]);

  // Zone management data
  const [zones, setZones] = useState<Zone[]>([
    {
      id: 'ws-1',
      name: 'Open Workspace A',
      type: 'workspace',
      capacity: 50,
      currentOccupancy: 42,
      peakUtilization: 90,
      recommendedCapacity: 45,
      adjacentZones: ['mt-1'],
      x: 0,
      y: 0
    },
    {
      id: 'mt-1',
      name: 'Meeting Hub',
      type: 'meeting',
      capacity: 30,
      currentOccupancy: 18,
      peakUtilization: 75,
      recommendedCapacity: 25,
      adjacentZones: ['ws-1', 'col-1'],
      x: 1,
      y: 0
    },
    {
      id: 'col-1',
      name: 'Collaboration Zone',
      type: 'collaboration',
      capacity: 25,
      currentOccupancy: 22,
      peakUtilization: 95,
      recommendedCapacity: 30,
      adjacentZones: ['mt-1'],
      x: 2,
      y: 0
    }
  ]);

  // Simulation states
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationDate, setSimulationDate] = useState<string>('2024-12');
  const [simulationMetrics, setSimulationMetrics] = useState<SimulationMetrics>({
    totalCapacity: 105,
    utilizationRate: 78,
    collaborationScore: 82,
    congestionRisk: 'low',
    costImpact: 0
  });
  const [simulationHistory, setSimulationHistory] = useState<{departments: Department[], zones: Zone[]}[]>([]);

  // UI states
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [showNewZoneForm, setShowNewZoneForm] = useState(false);
  const [draggedZone, setDraggedZone] = useState<string | null>(null);

  // Handlers for department planning
  const handleDepartmentGrowth = (deptId: string, plannedHeadcount: number, plannedDate: string) => {
    setDepartments(departments.map(dept => 
      dept.id === deptId 
        ? { ...dept, plannedHeadcount, plannedGrowthDate: plannedDate }
        : dept
    ));
    updateSimulationMetrics();
  };

  // Handlers for zone manipulation
  const handleAddZone = (newZone: Omit<Zone, 'id'>) => {
    const id = `zone-${zones.length + 1}`;
    setZones([...zones, { ...newZone, id }]);
    updateSimulationMetrics();
  };

  const handleUpdateZone = (zoneId: string, updates: Partial<Zone>) => {
    setZones(zones.map(zone => 
      zone.id === zoneId ? { ...zone, ...updates } : zone
    ));
    updateSimulationMetrics();
  };

  const handleDeleteZone = (zoneId: string) => {
    setZones(zones.filter(zone => zone.id !== zoneId));
    updateSimulationMetrics();
  };

  const handleMoveZone = (zoneId: string, newX: number, newY: number) => {
    setZones(zones.map(zone =>
      zone.id === zoneId ? { ...zone, x: newX, y: newY } : zone
    ));
    updateSimulationMetrics();
  };

  // Simulation handlers
  const startSimulation = () => {
    setIsSimulating(true);
    setSimulationHistory([{ departments: [...departments], zones: [...zones] }]);
    updateSimulationMetrics();
  };

  const updateSimulationMetrics = () => {
    // Calculate new metrics based on current state
    const totalCapacity = zones.reduce((sum, zone) => sum + zone.capacity, 0);
    const avgUtilization = zones.reduce((sum, zone) => sum + (zone.currentOccupancy / zone.capacity), 0) / zones.length * 100;
    const plannedHeadcount = departments.reduce((sum, dept) => sum + (dept.plannedHeadcount || dept.currentCapacity), 0);
    
    const newMetrics: SimulationMetrics = {
      totalCapacity,
      utilizationRate: Math.min(100, (plannedHeadcount / totalCapacity) * 100),
      collaborationScore: calculateCollaborationScore(),
      congestionRisk: calculateCongestionRisk(plannedHeadcount, totalCapacity),
      costImpact: calculateCostImpact()
    };
    
    setSimulationMetrics(newMetrics);
  };

  const calculateCollaborationScore = () => {
    // Calculate based on zone proximity and type distribution
    return Math.floor(70 + Math.random() * 20); // Simplified for example
  };

  const calculateCongestionRisk = (headcount: number, capacity: number) => {
    const ratio = headcount / capacity;
    if (ratio > 0.9) return 'high';
    if (ratio > 0.7) return 'medium';
    return 'low';
  };

  const calculateCostImpact = () => {
    // Calculate cost impact of changes
    return Math.floor(Math.random() * 10000); // Simplified for example
  };

  const undoLastChange = () => {
    if (simulationHistory.length > 0) {
      const lastState = simulationHistory[simulationHistory.length - 1];
      setDepartments(lastState.departments);
      setZones(lastState.zones);
      setSimulationHistory(simulationHistory.slice(0, -1));
      updateSimulationMetrics();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Simulation Controls */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-4">
        <h2 className="text-2xl font-bold">Space Planning Simulator</h2>
        <div className="flex items-center space-x-4">
          <input
            type="month"
            value={simulationDate}
            onChange={(e) => setSimulationDate(e.target.value)}
            className="border rounded px-2 py-1"
          />
          <button
            onClick={startSimulation}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {isSimulating ? 'Update Simulation' : 'Start Simulation'}
          </button>
          {isSimulating && (
            <button
              onClick={undoLastChange}
              className="text-gray-600 hover:text-gray-800"
              disabled={simulationHistory.length === 0}
            >
              <Undo className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Simulation Metrics */}
      {isSimulating && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Capacity</div>
            <div className="text-2xl font-bold">{simulationMetrics.totalCapacity}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Utilization Rate</div>
            <div className="text-2xl font-bold">{simulationMetrics.utilizationRate.toFixed(1)}%</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Collaboration Score</div>
            <div className="text-2xl font-bold">{simulationMetrics.collaborationScore}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Congestion Risk</div>
            <div className={`text-2xl font-bold ${
              simulationMetrics.congestionRisk === 'high' ? 'text-red-500' :
              simulationMetrics.congestionRisk === 'medium' ? 'text-yellow-500' :
              'text-green-500'
            }`}>
              {simulationMetrics.congestionRisk.toUpperCase()}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Cost Impact</div>
            <div className="text-2xl font-bold">${simulationMetrics.costImpact.toLocaleString()}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Department Growth Planning */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Department Growth Planning</h3>
          </div>
          <div className="space-y-4">
            {departments.map(dept => (
              <div 
                key={dept.id}
                className={`border rounded-lg p-4 ${selectedDepartment === dept.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setSelectedDepartment(dept.id)}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium">{dept.name}</h4>
                  <div className="text-sm">
                    <input
                      type="number"
                      value={dept.plannedHeadcount || dept.currentCapacity}
                      onChange={(e) => handleDepartmentGrowth(
                        dept.id,
                        Number(e.target.value),
                        simulationDate
                      )}
                      className="w-20 px-2 py-1 border rounded"
                      placeholder="Planned"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current</span>
                    <span>{dept.currentCapacity}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Growth</span>
                    <span className="text-green-500">+{dept.growthProjection}%</span>
                  </div>
                  {dept.plannedHeadcount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Planned Growth</span>
                      <span className={dept.plannedHeadcount > dept.currentCapacity ? 'text-green-500' : 'text-red-500'}>
                        {((dept.plannedHeadcount - dept.currentCapacity) / dept.currentCapacity * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Zone Management */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Zone Management</h3>
            <button
              onClick={() => setShowNewZoneForm(true)}
              className="flex items-center text-blue-500 hover:text-blue-600"
            >
              <Plus className="w-5 h-5 mr-1" />
              Add Zone
            </button>
          </div>
          
          {showNewZoneForm && (
            <div className="mb-4 border rounded-lg p-4">
              <h4 className="font-medium mb-2">New Zone</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Zone Name"
                  className="w-full px-2 py-1 border rounded"
                  onChange={(e) => setEditingZone(prev => prev ? { ...prev, name: e.target.value } : null)}
                />
                <select
                  className="w-full px-2 py-1 border rounded"
                  onChange={(e) => setEditingZone(prev => prev ? { ...prev, type: e.target.value as Zone['type'] } : null)}
                >
                  <option value="">Select Type</option>
                  <option value="workspace">Workspace</option>
                  <option value="meeting">Meeting</option>
                  <option value="collaboration">Collaboration</option>
                  <option value="amenity">Amenity</option>
                </select>
                <input
                  type="number"
                  placeholder="Capacity"
                  className="w-full px-2 py-1 border rounded"
                  onChange={(e) => setEditingZone(prev => prev ? { ...prev, capacity: Number(e.target.value) } : null)}
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      if (editingZone) {
                        handleAddZone(editingZone);
                        setShowNewZoneForm(false);
                        setEditingZone(null);
                      }
                    }}
                    className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setShowNewZoneForm(false);
                      setEditingZone(null);
                    }}
                    className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {zones.map(zone => (
              <div 
                key={zone.id}
                className={`border rounded-lg p-4 ${selectedZone === zone.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setSelectedZone(zone.id)}
                draggable
                onDragStart={() => setDraggedZone(zone.id)}
                onDragEnd={() => setDraggedZone(null)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{zone.name}</h4>
                    <span className="text-sm text-gray-500 capitalize">{zone.type}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingZone(zone);
                      }}
                      className="text-blue-500 hover:text-blue-600"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteZone(zone.id);
                      }}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {editingZone?.id === zone.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editingZone.name}
                      onChange={(e) => setEditingZone({ ...editingZone, name: e.target.value })}
                      className="w-full px-2 py-1 border rounded"
                    />
                    <select
                      value={editingZone.type}
                      onChange={(e) => setEditingZone({ ...editingZone, type: e.target.value as Zone['type'] })}
                      className="w-full px-2 py-1 border rounded"
                    >
                      <option value="workspace">Workspace</option>
                      <option value="meeting">Meeting</option>
                      <option value="collaboration">Collaboration</option>
                      <option value="amenity">Amenity</option>
                    </select>
                    <input
                      type="number"
                      value={editingZone.capacity}
                      onChange={(e) => setEditingZone({ ...editingZone, capacity: Number(e.target.value) })}
                      className="w-full px-2 py-1 border rounded"
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => {
                          handleUpdateZone(zone.id, editingZone);
                          setEditingZone(null);
                        }}
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingZone(null)}
                        className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Capacity</span>
                      <span>{zone.capacity}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Current</span>
                      <span>{zone.currentOccupancy}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Peak Usage</span>
                      <span>{zone.peakUtilization}%</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupancyManager; 